import { Popover, PopoverTrigger, PopoverContent } from '@radix-ui/react-popover';
import {
  Drama,
  Eye,
  FileText,
  MapPin,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react';

import {
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
} from '../../../../../../../../backend/src/case/inconsistency/inconsistency-display';
import { useUpdateInconsistency } from '@/api/mutations/useUpdateInconsistency';
import Calendar from '@/assets/icons/calendar.svg?react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import { cn } from '@/helpers/shadCnUtils';

export const InconsistencyLevelIndicator = ({
  inconsistencyId,
  caseId,
  level,
}: {
  inconsistencyId: string;
  caseId: string;
  level: InconsistencyLevel;
}) => {
  const { mutate: updateInconsistency } = useUpdateInconsistency();

  const updateInconsistencyLevel = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    newLevel: InconsistencyLevel,
  ) => {
    event.stopPropagation();
    updateInconsistency({
      inconsistencyId,
      caseId,
      body: {
        level: newLevel,
      },
    });
  };

  const config = {
    [InconsistencyLevel.Minor]: {
      Icon: ShieldQuestion,
      label: 'Minor',
      className: 'bg-gray-100 hover:border-gray-400 text-gray-700 border-gray-300',
    },
    [InconsistencyLevel.Severe]: {
      Icon: Shield,
      label: 'Severe',
      className: 'bg-amber-100 hover:border-amber-400 text-amber-700 border-amber-300',
    },
    [InconsistencyLevel.Critical]: {
      Icon: ShieldAlert,
      label: 'Critical',
      className: 'bg-red-100 text-red-700 border-red-300 hover:border-red-400',
    },
  };

  const { Icon, label, className } = config[level];

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Popover>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <div
                className={`flex w-fit items-center gap-1 rounded-md border px-1.5 py-0.5 transition-colors ${className}`}
                role="status"
                aria-label={`Inconsistencies level: ${label}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon className="h-4 w-4" />
                <div className="-mb-0.5 font-semibold">{label}</div>
              </div>
            </TooltipTrigger>
          </PopoverTrigger>

          <TooltipContent>
            <p>Edit severity</p>
          </TooltipContent>
        </Tooltip>

        <PopoverContent className="z-50 mt-1 flex flex-col gap-1 rounded-md border-[1px] border-gray-200 bg-white p-1 outline-none">
          <div
            className="flex items-center gap-2 hover:text-yellow-500"
            onClick={(e) => updateInconsistencyLevel(e, InconsistencyLevel.Minor)}
          >
            <ShieldQuestion className="h-4 w-4 text-yellow-500" />
            <div>Minor</div>
          </div>
          <div
            className="flex items-center gap-2 hover:text-orange-500"
            onClick={(e) => updateInconsistencyLevel(e, InconsistencyLevel.Severe)}
          >
            <ShieldCheck className="h-4 w-4 text-orange-500" />
            <div>Severe</div>
          </div>
          <div
            className="flex items-center gap-2 hover:text-red-600"
            onClick={(e) => updateInconsistencyLevel(e, InconsistencyLevel.Critical)}
          >
            <ShieldAlert className="h-4 w-4 text-red-600" />
            <div>Critical</div>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};

export const EvidenceCountIndicator = ({ count, className }: { count: number; className?: string }) => {
  return (
    <div className={cn('group rounded-md px-1 py-0.5', className)}>
      <div className="flex items-center gap-2">
        <span className="text-gray-600">
          <b className="text-gray-700">{count}</b> pieces of evidence
        </span>
      </div>
    </div>
  );
};

export const InconsistencyModeIndicator = ({
  mode,
  onClick,
}: {
  mode: InconsistencySource;
  onClick?: (mode: InconsistencySource) => void;
}) => {
  const color = {
    [InconsistencySource.MultipleDepositions]:
      'bg-purple-100 border border-purple-200 hover:bg-purple-200/80 text-purple-800',
    [InconsistencySource.SameDeposition]: 'bg-blue-100 border border-blue-200 hover:bg-blue-200/80 text-blue-800',
    [InconsistencySource.DepositionVsEvidence]:
      'bg-orange-100 border border-orange-200 hover:bg-orange-200/80 text-orange-800',
    [InconsistencySource.EvidenceVsEvidence]: 'bg-pink-100 border border-pink-200 hover:bg-pink-200/80 text-pink-800',
  }[mode];

  return (
    <div
      className={`rounded-md px-2 py-0.5 ${color} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(mode);
      }}
    >
      <p>{getInconsistencySourceDisplayName(mode)}</p>
    </div>
  );
};

export const InconsistencyTypeIndicator = ({
  type,
  onClick,
}: {
  type: InconsistencyType;
  onClick?: (type: InconsistencyType) => void;
}) => {
  const config = {
    [InconsistencyType.Dates]: {
      Icon: Calendar,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Dates),
      className: 'bg-purple-100 hover:bg-purple-200/80 text-purple-800 border-purple-300',
    },
    [InconsistencyType.Events]: {
      Icon: MapPin,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Events),
      className: 'bg-blue-100 hover:bg-blue-200/80 text-blue-900 border-blue-300',
    },
    [InconsistencyType.Evolving]: {
      Icon: RefreshCw,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Evolving),
      className: 'bg-green-100 hover:bg-green-200/80 text-green-900 border-green-300',
    },
    [InconsistencyType.Details]: {
      Icon: FileText,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Details),
      className: 'bg-yellow-100 hover:bg-yellow-200/80 text-yellow-900 border-yellow-300',
    },
    [InconsistencyType.Observations]: {
      Icon: Eye,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Observations),
      className: 'bg-pink-100 hover:bg-pink-200/80 text-pink-900 border-pink-300',
    },
    [InconsistencyType.Fabrications]: {
      Icon: Drama,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Fabrications),
      className: 'bg-red-100 hover:bg-red-200/80 text-red-900 border-red-300',
    },
  };

  const { Icon, label, className } = config[type];
  return (
    <div
      className={`flex items-center gap-1.5 rounded-md border px-2 py-0.5 transition-colors ${className} ${onClick ? 'cursor-pointer' : ''}`}
      role="status"
      aria-label={`Inconsistencies type: ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(type);
      }}
    >
      <Icon className="size-3" />
      <span className="pt-0.25">{label}</span>
    </div>
  );
};
